import React, { useEffect, useState } from "react";
import { useLazyExportFnfTransactionsDataQuery, useSearchFnfTransactionApiQuery, useUpdateTransactionStatusMutation } from "./transactionsApi";
import { useGetEnumsQuery } from "../../../app/globalApi";
import { useLazyExportRevenueEventDataQuery } from "../revenueEvents/revenueEventsApi";
import { DataTableColumn } from "../../../components/common/datatable/DataTableTypes";
import { createClickableLink } from "../../../components/common/datatable/DataTableUtils";
import { SearchInputWrapper } from "../../../components/common/datatable/filter/SearchInputWrapper";
import { SearchSelectWithEnum, SearchSelectWithList } from "../../../components/common/datatable/filter/SearchSelectWrapper";
import { formatValueAsDateAndTime } from "../../../helpers/utils/DateUtils";
import { SearchDatePickerWrapper } from "../../../components/common/datatable/filter/SearchDatePickerWrapper";
import { getEnumDisplayNameByConstant } from "../../../helpers/utils/EnumUtils";
import Breadcrumb from "../../../components/common/Breadcrumbs";
import { DataTable } from "../../../components/common/datatable/DataTable";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import { useGetAllTenantsQuery } from "../../customer/tenantApi";
import { useLazyGetAllCustomerByTenantQuery } from "../../customer/customerApi";
import FnfTransactionInfoModal from "./modal/FnfTransactionInfoModal";
import { useLazyGetBranchByCustomerIdQuery } from "../../customer/branchContactApi";
import { useFindUserSettingsByGroupNameQuery } from "../../../components/common/userSetting/userSettingsApi";
import {hasPermission} from "../../../helpers/utils/AccessControlUtils";


function Transactions() {

    const [crumbs] = useState([
        { content: "Home", url: "/home" },
        { content: "CRRAR Transactions", url: "" }]
    )
    const tenantData = useGetAllTenantsQuery("");
    const [customerDataApi, customerData] = useLazyGetAllCustomerByTenantQuery();
    const [branchDataApi, branchData] = useLazyGetBranchByCustomerIdQuery();
    const { data: enums } = useGetEnumsQuery("");
    const [customerOrderNumber, setCustomerOrderNumber] = useState(null);
    const [productType, setProductType] = useState(null);
    const [creationDate, setCreationDate] = useState(null);
    const [amount, setAmount] = useState(null);
    const [reason, setReason] = useState(null);
    const [transactionId, setTransactionId] = useState(null);
    const [transactionStatus, setTransactionStatus] = useState(null);
    const [showFnfInfoModal, setShowFnfInfoModal] = useState(false);
    const [showFnfStatusUpdateModal, setShowFnfStatusUpdateModal] = useState(false);
    const { data: userSetting } = useFindUserSettingsByGroupNameQuery("CRRAR Transactions");
    const [triggerExportReportDataQuery] = useLazyExportFnfTransactionsDataQuery();
    const [updateTransactionStatus, updateTransactionStatusApi] = useUpdateTransactionStatusMutation();

    useEffect(() => {
        document.title = 'CRRAR Transactions';

    }, []);

    const handleRetry = () => {
        if (transactionId && transactionStatus) {
            updateTransactionStatus({
                fnfTransactionId: transactionId,
                transactionStatus: "PENDING"
            })
        }
    };

    const columns: DataTableColumn[] = [

        {
            label: <span>Info</span>,
            accessor: "id",
            canSort: false,
            visibility:{hasShowSetting:false},
            cell: (props: any) => {
                return <i className="fa fa-info-circle cursor active_the_btn text-highlight-0 " onClick={(e) => {
                    setCustomerOrderNumber(props.row.original.refNo);
                    setProductType(props.row.original.productType);
                    setCreationDate(props.row.original.creationDate);
                    setAmount(props.row.original.amount);
                    setReason(props.row.original.reason);
                    setShowFnfInfoModal(true);
                }} />
            }
        },
        {
            label: "Order No.", accessor: "orderNo",  canSort: false,
            cell: (props: any) => {
                let path = "/orders/" + props.row.original.orderId;
                return createClickableLink(path, props.value, userSetting);
            },
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter Order No...."}
                    name={"tgxId"} accessor={"tgxId"} />,
                overrideFilterAccessor: "tgxId"
            }],
        },

        {
            label: "Ref. No.", accessor: "refNo",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter Ref. No...."}
                    name={"customerOrderNumber"} accessor={"customerOrderNumber"} />,
                overrideFilterAccessor: "customerOrderNumber"
            }],
        },
        {
            label: "Tenant",
            accessor: "tenant",
            visibility: { canColumnShow: false, hasShowSetting: false },
            filterOptions: [{
                dependentColumns: ["Bill To Customer"],
                filterComponent: SearchSelectWithList(tenantData?.data, "name", "name", "Select Tenant...", false, "tenant"),
                overrideFilterAccessor: "tenant",
                overrideFilterLabel: "Tenant",
            }],
        },
        {
            label: "Bill To Customer", accessor: "billToCustomer", visibility: { hasShowSetting: false },
            cell: (props: any) => {
                return props.value;
            },
            filterOptions: [{
                dependentColumns: ["Branch"],
                filterComponent: SearchSelectWithList(customerData?.data, (customer: { customerName: any; customerNumber: any; }) => `${customer.customerName} (${customer.customerNumber})`, "id", "Select Customer...", false, "customerId"),
                onDependencyUpdated: (value: any) => {
                    customerDataApi({ tenantName: value })
                },
                overrideFilterAccessor: "customerId",
            }],
        },
        {
            label: "Branch", accessor: "branch", visibility: { hasShowSetting: false },
            cell: (props: { value: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; }) => {
                return <span>{props.value}</span>;
            },
            filterOptions: [{
                filterComponent: SearchSelectWithList(branchData?.data?.data, "name", "id", "Select Branches...", true, "branchIds"),
                onDependencyUpdated: (value: any) => {
                    branchDataApi(value)
                },
                overrideFilterAccessor: "branchIds",
            }]
        },

        {
            label: "Creation Date", accessor: "creationDate", cell: (props: { row: { original: { creationDate: any; }; }; }) => {
                return formatValueAsDateAndTime(props.row.original.creationDate);
            },
            filterOptions: [
                {
                    filterComponent: <SearchDatePickerWrapper accessor={"createdDateFrom"} />,
                    overrideFilterAccessor: "createdDateFrom",
                    overrideFilterLabel: "Created Date - From",
                },
                {
                    filterComponent: <SearchDatePickerWrapper accessor={"createdDateTo"} />,
                    overrideFilterAccessor: "createdDateTo",
                    overrideFilterLabel: "Created Date - To",
                }]
        },
        {
            label: "Transmit Date", accessor: "transmitDate",
            cell: (props: { row: { original: { transmitDate: any; }; }; }) => {
                return formatValueAsDateAndTime(props.row.original.transmitDate);
            },
            filterOptions: [
                {
                    filterComponent: <SearchDatePickerWrapper accessor={"transmitDateFrom"} />,
                    overrideFilterAccessor: "transmitDateFrom",
                    overrideFilterLabel: "Transmit Date - From",
                },
                {
                    filterComponent: <SearchDatePickerWrapper accessor={"transmitDateTo"} />,
                    overrideFilterAccessor: "transmitDateTo",
                    overrideFilterLabel: "Transmit Date - To",
                }]
        },
        { label: "Attempts", accessor: "timesAttempted", visibility: { hasShowSetting: false } },
        {
            label: "Reason", accessor: "reason", visibility: { canColumnShow: false },
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter Reason...."}
                    name={"message"} accessor={"message"} />,
                overrideFilterAccessor: "message"
            }],
        },

        {
            label: "Status", accessor: "status", visibility: { hasShowSetting: false },
            cell: (props: { value: any; }) => {
                return <>
                    <span>
                        {getEnumDisplayNameByConstant(enums?.["FnfTransactionStatus"], props.value)}
                    </span>
                </>
            },
            filterOptions: [{
                filterComponent: SearchSelectWithEnum(enums?.["FnfTransactionStatus"], "Select FNF Transaction Status...", true, "statuses"),
                overrideFilterLabel: "FNF Transaction Status",
                overrideFilterAccessor: "statuses",

            }],
        },

        {
            label: <span>Action</span>,
            accessor: "orderId",
            canSort: false,
            visibility:{hasShowSetting:false},
            cell: (props: { row: { original: { status: string; id: React.SetStateAction<null>; }; }; }) => {
                return (!(hasPermission(["FNFTRANS-U"])) || (props.row.original.status != "FAILED")) ?
                    <span
                        className={"border-transparent cursor-pointer rounded-[11px] shadow-[0_0_4px_#0003]  m-[7px_4px] px-[12px] py-[3px] bg-text-1"}>Retry</span>
                    :
                    <span onClick={(e) => {
                        setTransactionId(props.row.original.id);
                        setTransactionStatus(getEnumDisplayNameByConstant(enums?.["FnfTransactionStatus"], props.row.original.status));
                        setShowFnfStatusUpdateModal(true);
                    }}
                        className={"border-transparent cursor-pointer rounded-[11px] shadow-[0_0_4px_#0003] text-[white] m-[7px_4px] px-[12px] py-[3px] bg-text-4"}>Retry</span>

            }
        },

    ];
    return (
        <>
            <div className="px-4 py-2">
                <div className="flex justify-between mb-2.5">
                    <Breadcrumb crumbs={crumbs} />
                </div>
                <DataTable
                    columns={columns}
                    actions={{
                        fetchAction: useSearchFnfTransactionApiQuery,
                        exportAction: triggerExportReportDataQuery
                    }
                    }

                    userSettingGroup={"CRRAR Transactions"}
                    basePermission={"FNFTRANS"}
                />
            </div>
            {showFnfInfoModal && <FnfTransactionInfoModal show={showFnfInfoModal}
                setShow={setShowFnfInfoModal}
                customerOrderNumber={customerOrderNumber}
                productType={productType}
                creationDate={creationDate}
                amount={amount}
                reason={reason}
            />
            }
            {showFnfStatusUpdateModal && <ConfirmationModal title={"Retry Confirmation"}
                bodyMessage={"Are you sure you want to retry?"}
                onSubmit={handleRetry} show={showFnfStatusUpdateModal}
                setShow={setShowFnfStatusUpdateModal} isLoading={updateTransactionStatusApi.isLoading}
            />
            }
            <div />
        </>
    )

}

export default Transactions;